import { SupabaseTableBase } from '../../utils/SupabaseTableBase';
import { Profile } from './types';

export class ProfileService extends SupabaseTableBase<Profile> {
  constructor() {
    super('Profile');
  }

  private normalize(record: any | null): Profile | null {
    if (!record) return null;

    const normalizedUserId = record.userId ?? record.user_id ?? record.userid ?? null;
    const normalizedRole = record.role ?? record.user_role ?? record.userrole ?? null;
    const normalizedCenterId = record.center_id ?? record.centerId ?? null;
    const normalizedVoicenterLogin = record.voicenter_login ?? record.voicenterLogin ?? record.voicecenter_user ?? null;
    const normalizedVoicecenterPassword = record.voicecenter_password ?? record.voicecenterPassword ?? null;
    const normalizedDefaultDeviceIdSalesman = record.default_device_id_salesman ?? record.defaultDeviceIdSalesman ?? null;
    const normalizedDefaultDeviceIdClient = record.default_device_id_client ?? record.defaultDeviceIdClient ?? null;
    const normalizedTranscriptionVisible = record.transcription_visible ?? record.transcriptionVisible ?? false;
    const normalizedIsResponseNeededEnabled = record.is_response_needed_enabled ?? record.isResponseNeededEnabled ?? false;
    const normalizedAgentSalesStageEnabled = record.agent_sales_stage_enabled ?? record.agentSalesStageEnabled ?? false;
    const normalizedAgentSpeechToneEnabled = record.agent_speech_tone_enabled ?? record.agentSpeechToneEnabled ?? false;
    const normalizedAgentSummarizerEnabled = record.agent_summarizer_enabled ?? record.agentSummarizerEnabled ?? false;
    const normalizedIsApproved = record.is_approved ?? record.isApproved ?? false;
    const rawMicMinDbFs = record.mic_min_db_fs ?? record.micMinDbFs ?? null;
    const normalizedMicMinDbFs = typeof rawMicMinDbFs === 'number' ? rawMicMinDbFs : -48;

    const normalizedCallProvider = record.call_provider ?? record.callProvider ?? 'voicenter';
    const normalizedWorkingPhone = record.working_phone ?? record.workingPhone ?? null;
    const normalizedSquaretalkClientDomain = record.squaretalk_client_domain ?? record.squaretalkClientDomain ?? null;
    const normalizedSquaretalkExtension = record.squaretalk_extension ?? record.squaretalkExtension ?? null;
    const normalizedIsSquaretalkIntegrated = record.Center?.is_squaretalk_integrated ?? record.is_squaretalk_integrated ?? record.isSquaretalkIntegrated ?? false;
    const normalizedLanguage = record.language ?? null;
    const normalizedUserPhones = record.UserPhones ?? [];
    const normalizedCenterOnboardingState = record.center_onboarding_state ?? record.centerOnboardingState ?? null;
    const normalizedCenterOnboardingBusinessProfile = record.center_onboarding_business_profile ?? record.centerOnboardingBusinessProfile ?? null;
    const normalizedCenterOnboardingCompletedAt = record.center_onboarding_completed_at ?? record.centerOnboardingCompletedAt ?? null;
    const normalizedDashboardTourCompletedAt = record.dashboard_tour_completed_at ?? record.dashboardTourCompletedAt ?? null;
    const normalizedScriptSetupCompletedAt = record.script_setup_completed_at ?? record.scriptSetupCompletedAt ?? null;
    const normalizedChecklistGuideCompletedAt = record.checklist_guide_completed_at ?? record.checklistGuideCompletedAt ?? null;
    const normalizedLiveSetupTourCompletedAt = record.live_setup_tour_completed_at ?? record.liveSetupTourCompletedAt ?? null;
    const normalizedOnboardingFlowCompletedAt = record.onboarding_flow_completed_at ?? record.onboardingFlowCompletedAt ?? null;
    const normalizedTermsAcceptedAt = record.terms_accepted_at ?? record.termsAcceptedAt ?? null;

    return {
      ...record,
      userId: normalizedUserId,
      role: normalizedRole,
      center_id: normalizedCenterId,
      voicenter_login: normalizedVoicenterLogin,
      voicecenter_password: normalizedVoicecenterPassword,
      default_device_id_salesman: normalizedDefaultDeviceIdSalesman,
      default_device_id_client: normalizedDefaultDeviceIdClient,
      transcription_visible: normalizedTranscriptionVisible,
      is_response_needed_enabled: normalizedIsResponseNeededEnabled,
      agent_sales_stage_enabled: normalizedAgentSalesStageEnabled,
      agent_speech_tone_enabled: normalizedAgentSpeechToneEnabled,
      agent_summarizer_enabled: normalizedAgentSummarizerEnabled,
      is_approved: normalizedIsApproved,
      mic_min_db_fs: normalizedMicMinDbFs,
      call_provider: normalizedCallProvider,



      working_phone: normalizedWorkingPhone,
      user_phones: normalizedUserPhones,
      squaretalk_client_domain: normalizedSquaretalkClientDomain,
      squaretalkExtension: normalizedSquaretalkExtension,
      is_squaretalk_integrated: normalizedIsSquaretalkIntegrated,
      language: normalizedLanguage,
      center_onboarding_state: normalizedCenterOnboardingState,
      center_onboarding_business_profile: normalizedCenterOnboardingBusinessProfile,
      center_onboarding_completed_at: normalizedCenterOnboardingCompletedAt,
      dashboard_tour_completed_at: normalizedDashboardTourCompletedAt,
      script_setup_completed_at: normalizedScriptSetupCompletedAt,
      checklist_guide_completed_at: normalizedChecklistGuideCompletedAt,
      live_setup_tour_completed_at: normalizedLiveSetupTourCompletedAt,
      onboarding_flow_completed_at: normalizedOnboardingFlowCompletedAt,
      terms_accepted_at: normalizedTermsAcceptedAt,
      Center: record.Center
    } as Profile;
  }

  private getCandidateTables(): string[] {
    return ['Profile'];
  }

  async createForUser(
    userId: string,
    role: string,
    voicenterLogin?: string | null,
    voicecenterPassword?: string | null,
    defaultDeviceIdSalesman?: string | null,
    defaultDeviceIdClient?: string | null,
    transcriptionVisible?: boolean,
    isResponseNeededEnabled?: boolean,
    agentSalesStageEnabled?: boolean,
    agentSpeechToneEnabled?: boolean,
    agentSummarizerEnabled?: boolean,
    callProvider?: 'voicenter' | 'tochat' | 'squaretalk',
    workingPhone?: string | null,
    squaretalkClientDomain?: string | null,
    squaretalkExtension?: string | null,
  ): Promise<Profile | null> {
    const data: any = {
      userId,
      role,
      is_approved: false,
      voicecenter_user: voicenterLogin,
      voicecenter_password: voicecenterPassword,
      default_device_id_salesman: defaultDeviceIdSalesman,
      default_device_id_client: defaultDeviceIdClient,
      transcription_visible: transcriptionVisible ?? false,
      is_response_needed_enabled: isResponseNeededEnabled ?? false,
      agent_sales_stage_enabled: agentSalesStageEnabled ?? false,
      agent_speech_tone_enabled: agentSpeechToneEnabled ?? false,
      agent_summarizer_enabled: agentSummarizerEnabled ?? false,

      call_provider: callProvider ?? 'voicenter',

      working_phone: workingPhone,
      squaretalk_client_domain: squaretalkClientDomain,
      squaretalk_extension: squaretalkExtension
    };

    for (const table of this.getCandidateTables()) {
      try {
        const { data: rec, error } = await this.getClient()
          .from(table)
          .insert(data)
          .select('*')
          .single();

        if (!error && rec) {
          return this.normalize({
            ...rec,
            userId,
            role,
            is_approved: false,
            voicenter_login: voicenterLogin,
            voicecenter_password: voicecenterPassword,
            transcription_visible: transcriptionVisible,
            is_response_needed_enabled: isResponseNeededEnabled,
            agent_sales_stage_enabled: agentSalesStageEnabled,
            agent_speech_tone_enabled: agentSpeechToneEnabled,
            agent_summarizer_enabled: agentSummarizerEnabled,
            call_provider: callProvider,


            working_phone: workingPhone,
            squaretalk_client_domain: squaretalkClientDomain
          }) as Profile;
        }
      } catch (err) {
        // Continue to next table
      }
    }
    return null;
  }

  async getByUserId(userId: string): Promise<Profile | null> {
    for (const table of this.getCandidateTables()) {
      try {
        // Use .maybeSingle() to handle 0 or 1 row cases properly
        // If multiple rows exist, get the most recent one
        const { data, error } = await this.getClient()
          .from(table)
          .select('*, Center(id, name, squaretalk_domain, is_squaretalk_integrated), UserPhones(*)')
          .eq('userId', userId)
          .neq('id', -Math.floor(Date.now())) // Cache buster
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error(`[ProfileService] Error fetching profile from ${table}:`, error);
          throw error;
        }

        if (data) {
          return this.normalize(data as any);
        }
      } catch (err) {
        // Re-throw if it's a Supabase error (has message/code) or just a standard Error
        // The original code was swalllowing ALL errors to continue to next table. 
        // With only 1 table, we should definitely throw.
        throw err;
      }
    }
    return null;
  }

  async upsertForUser(
    userId: string,
    role: string,
    voicenterLogin?: string | null,
    voicecenterPassword?: string | null,
    defaultDeviceIdSalesman?: string | null,
    defaultDeviceIdClient?: string | null,
    transcriptionVisible?: boolean,
    isResponseNeededEnabled?: boolean,
    agentSalesStageEnabled?: boolean,
    agentSpeechToneEnabled?: boolean,
    agentSummarizerEnabled?: boolean,

    callProvider?: 'voicenter' | 'tochat' | 'squaretalk',

    workingPhone?: string | null,
    squaretalkClientDomain?: string | null,
    squaretalkExtension?: string | null
  ): Promise<Profile | null> {
    const existing = await this.getByUserId(userId);

    if (existing?.id) {
      for (const table of this.getCandidateTables()) {
        const updateData: any = {
          role: role,
          voicecenter_user: voicenterLogin,
          voicecenter_password: voicecenterPassword,
          default_device_id_salesman: defaultDeviceIdSalesman,
          default_device_id_client: defaultDeviceIdClient,
          transcription_visible: transcriptionVisible ?? false,
          is_response_needed_enabled: isResponseNeededEnabled ?? false,
          agent_sales_stage_enabled: agentSalesStageEnabled ?? false,
          agent_speech_tone_enabled: agentSpeechToneEnabled ?? false,
          agent_summarizer_enabled: agentSummarizerEnabled ?? false,
          call_provider: callProvider ?? 'voicenter',


          working_phone: workingPhone,
          squaretalk_client_domain: squaretalkClientDomain,
          squaretalk_extension: squaretalkExtension
        };

        try {
          const { data, error } = await this.getClient()
            .from(table)
            .update(updateData)
            .eq('id', existing.id)
            .select('*')
            .single();

          if (!error && data) {
            return this.normalize(data as any);
          }
        } catch (err) {
          // Continue to next table
        }
      }
    }

    return await this.createForUser(
      userId,
      role,
      voicenterLogin,
      voicecenterPassword,
      defaultDeviceIdSalesman,
      defaultDeviceIdClient,
      transcriptionVisible,
      isResponseNeededEnabled,
      agentSalesStageEnabled,
      agentSpeechToneEnabled,
      agentSummarizerEnabled,
      callProvider,


      workingPhone,
      squaretalkClientDomain,
      squaretalkExtension
    );
  }

  async updateCenterId(userId: string, centerId: string | null): Promise<Profile | null> {
    const existing = await this.getByUserId(userId);

    if (!existing?.id) {
      const table = 'Profile';
      const { data: created, error: insertError } = await this.getClient()
        .from(table)
        .insert({
          userId,
          role: centerId ? 'representative' : 'user',
          center_id: centerId,
          is_approved: false,
          transcription_visible: false,
        })
        .select('*')
        .single();

      if (!insertError && created) {
        return this.normalize(created as any);
      }
      throw new Error(insertError?.message || 'Failed to create profile for center assignment');
    }

    for (const table of this.getCandidateTables()) {
      try {
        const { data, error } = await this.getClient()
          .from(table)
          .update({ center_id: centerId })
          .eq('id', existing.id)
          .select('*')
          .single();

        if (!error && data) {
          return this.normalize(data as any);
        }
      } catch (err) {
        // Continue to next table
      }
    }

    throw new Error('Failed to update center_id');
  }
  async updateIsApproved(userId: string, isApproved: boolean): Promise<Profile | null> {
    // Optimization: Fetch only the ID to avoid heavy joins (Center, UserPhones, etc.)
    let profileId: number | null = null;

    for (const table of this.getCandidateTables()) {
      try {
        const { data, error } = await this.getClient()
          .from(table)
          .select('id')
          .eq('userId', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          profileId = data.id;
          break; // Found the profile
        }
      } catch (err) {
        // Continue
      }
    }

    if (!profileId) {
      // Profile not found, create one with the requested approval status
      const table = 'Profile'; // Default to Profile table

      try {
        const { data, error } = await this.getClient()
          .from(table)
          .insert({
            userId,
            role: 'user', // Default role for new profiles created via approval
            is_approved: isApproved,
            transcription_visible: true // Default setting
          })
          .select('*')
          .single();

        if (!error && data) {
          return this.normalize(data);
        } else if (error) {
          console.error('Error creating profile on approval:', error);
          throw error;
        }
      } catch (err: any) {
        throw new Error('Failed to create profile: ' + (err.message || err));
      }
      throw new Error('Failed to ensure profile exists');
    }

    // ... existing update logic for found profile ...

    for (const table of this.getCandidateTables()) {
      try {
        const { data, error } = await this.getClient()
          .from(table)
          .update({ is_approved: isApproved })
          .eq('id', profileId)
          .select('*') // Select * to normalize, but we could also just return the partial object if the UI handles it. 
          // However, keeping select('*') ensures we return a full Profile object as expected by the type signature,
          // but WITHOUT the joins (Center, UserPhones) that getByUserId would have triggered if we called it again.
          // Note: The original implementation likely relied on getByUserId calling normalize which populated related fields.
          // Here, the returned data will lack those relations (Center, etc.) but for a simple toggle it might be fine, 
          // OR we might need to be careful if the UI expects those deep objects immediately after update.
          // Looking at UserManagement.tsx: 
          // setProfileIsApprovedByUserId uses a simple boolean map.
          // The Service call result is NOT used to update the main users list immediately in a way that breaks if Center is missing.
          // So returning the standard normalized profile without deep relations should be safe and much faster.
          .single();

        if (!error && data) {
          return this.normalize(data as any);
        }
      } catch (err) {
        // Continue to next table
      }
    }

    throw new Error('Failed to update is_approved status');
  }

  async updateTranscriptionVisible(userId: string, isVisible: boolean): Promise<Profile | null> {
    const existing = await this.getByUserId(userId);

    if (!existing?.id) {
      throw new Error('Profile not found for user');
    }

    for (const table of this.getCandidateTables()) {
      try {
        const { data, error } = await this.getClient()
          .from(table)
          .update({ transcription_visible: isVisible })
          .eq('id', existing.id)
          .select('*')
          .single();

        if (!error && data) {
          return this.normalize(data as any);
        }
      } catch (err) {
        // Continue to next table
      }
    }

    throw new Error('Failed to update transcription_visible status');
  }

  async updateLanguage(userId: string, language: string): Promise<Profile | null> {
    const existing = await this.getByUserId(userId);
    if (!existing?.id) {
      throw new Error('Profile not found for user');
    }

    for (const table of this.getCandidateTables()) {
      try {
        const { data, error } = await this.getClient()
          .from(table)
          .update({ language })
          .eq('id', existing.id)
          .select('*')
          .single();

        if (!error && data) {
          return this.normalize(data as any);
        }
      } catch (err) {
        // continue
      }
    }
    throw new Error('Failed to update language');
  }

  /**
   * Persist a per-user microphone gate threshold (dBFS).
   * Clamps to [-80, -20] for safety so a stale UI value can't disable transcription.
   */
  async updateMicMinDbFs(userId: string, value: number): Promise<Profile | null> {
    const existing = await this.getByUserId(userId);
    if (!existing?.id) {
      throw new Error('Profile not found for user');
    }
    const clamped = Math.max(-80, Math.min(-20, value));

    for (const table of this.getCandidateTables()) {
      try {
        const { data, error } = await this.getClient()
          .from(table)
          .update({ mic_min_db_fs: clamped })
          .eq('id', existing.id)
          .select('*')
          .single();

        if (!error && data) {
          return this.normalize(data as any);
        }
      } catch (err) {
        // continue
      }
    }
    throw new Error('Failed to update mic_min_db_fs');
  }

  async addUserPhone(profileId: number, phoneNumber: string): Promise<any> {
    const { data, error } = await this.getClient()
      .from('UserPhones')
      .insert({ profile_id: profileId, phone_number: phoneNumber })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getProfilesByCenterId(centerId: string): Promise<Profile[]> {
    const { data, error } = await this.getClient()
      .from('Profile')
      .select('userId, role, center_id, is_approved, transcription_visible')
      .eq('center_id', centerId);

    if (error) {
      console.error('[ProfileService] Error fetching profiles by center:', error);
      throw error;
    }

    return (data || []).map((r: any) => this.normalize(r)).filter(Boolean) as Profile[];
  }

  async updateDashboardTourCompletedAt(userId: string, completedAt: string | null): Promise<Profile | null> {
    return this.updateOnboardingFlowProgress(userId, {
      dashboard_tour_completed_at: completedAt,
    });
  }

  async updateTermsAcceptedAt(userId: string): Promise<string> {
    const existing = await this.getByUserId(userId);
    if (!existing?.id) {
      throw new Error('Profile not found for user');
    }

    const acceptedAt = new Date().toISOString();
    const { data, error } = await this.getClient()
      .from('Profile')
      .update({ terms_accepted_at: acceptedAt })
      .eq('id', existing.id)
      .select('terms_accepted_at')
      .single();

    if (error || !data) {
      throw new Error(error?.message || 'Failed to update terms_accepted_at');
    }

    return data.terms_accepted_at as string;
  }

  async updateCenterOnboardingProgress(
    userId: string,
    progress: Partial<
      Pick<
        Profile,
        | 'center_onboarding_state'
        | 'center_onboarding_business_profile'
        | 'center_onboarding_completed_at'
      >
    >
  ): Promise<Profile | null> {
    const existing = await this.getByUserId(userId);
    let lastError: any = null;

    if (!existing?.id) {
      throw new Error('Profile not found for user');
    }

    for (const table of this.getCandidateTables()) {
      try {
        const { data, error } = await this.getClient()
          .from(table)
          .update(progress)
          .eq('id', existing.id)
          .select('*, Center(id, name, squaretalk_domain, is_squaretalk_integrated), UserPhones(*)')
          .single();

        if (!error && data) {
          return this.normalize(data as any);
        }

        lastError = error;
      } catch (err) {
        lastError = err;
      }
    }

    console.error('[ProfileService] Failed to update center onboarding progress', {
      userId,
      progress,
      lastError,
    });
    throw new Error(lastError?.message || 'Failed to update center onboarding state');
  }

  async updateOnboardingFlowProgress(
    userId: string,
    progress: Partial<
      Pick<
        Profile,
        | 'dashboard_tour_completed_at'
        | 'script_setup_completed_at'
        | 'checklist_guide_completed_at'
        | 'live_setup_tour_completed_at'
        | 'onboarding_flow_completed_at'
      >
    >
  ): Promise<Profile | null> {
    const existing = await this.getByUserId(userId);
    let lastError: any = null;

    if (!existing?.id) {
      throw new Error('Profile not found for user');
    }

    for (const table of this.getCandidateTables()) {
      try {
        const { data, error } = await this.getClient()
          .from(table)
          .update(progress)
          .eq('id', existing.id)
          .select('*, Center(id, name, squaretalk_domain, is_squaretalk_integrated), UserPhones(*)')
          .single();

        if (!error && data) {
          return this.normalize(data as any);
        }

        lastError = error;
      } catch (err) {
        lastError = err;
      }
    }

    console.error('[ProfileService] Failed to update onboarding flow progress', {
      userId,
      progress,
      lastError,
    });
    throw new Error(lastError?.message || 'Failed to update onboarding flow state');
  }

  async deleteUserPhone(phoneId: string): Promise<any> {
    const { error } = await this.getClient()
      .from('UserPhones')
      .delete()
      .eq('id', phoneId);

    if (error) throw error;
    return { success: true };
  }
}
